import { FlowState } from '@de/providers';
import { Placeholder, Step } from '@de/types';
import {
  getCurrentFieldValues,
  getDataByFieldFromFlow,
} from '@de/utils/dataFromFlowUtils';
import { getOptionByCustomField } from '@de/utils/getOptionByCustomField';
import { flattenStepsForDynamicOptions } from '@de/utils/flattenStepsForDynamicOptions';
import { getOptionValueString } from '@de/utils/queryParamUtils';
import { translateStringFields } from '@de/utils/translatePlaceholderForAttribute';

export interface TranslateStepContext {
  steps: Step[];
  flowState: FlowState;
  dcs?: { degree?: string; category?: string; subject?: string };
  audienceIntent?: {
    degreeDecisionOutcome?: string;
    subjectDecisionOutcome?: string;
    careerDecisionOutcome?: string;
  };
}

/**
 * Collects all custom field values from steps
 * @param steps - Array of all steps in the flow
 * @param flowState - Current flow state containing user selections
 * @returns Record mapping custom field names to their selected values
 */
const getCustomFieldValues = (
  steps: Step[],
  flowState: FlowState,
): Record<string, string> => {
  const customFieldValues: Record<string, string> = {};

  // Include nested MULTI_QUESTION / DCS children so Discover-seeded
  // customFields on `stepQuestions` participate in placeholder copy.
  flattenStepsForDynamicOptions(steps).forEach((step) => {
    if (step.customField) {
      const option = getOptionByCustomField(step.customField, steps, flowState);
      const value = getOptionValueString(option);
      if (value) {
        // Use lowercase key for consistent placeholder matching
        customFieldValues[step.customField.toLowerCase()] = value;
      }
    }
  });

  // Configurable Steps declare customField per DSL control, so the handle
  // lives on the stored Option rather than the parent Step. Include those
  // options so result-step placeholders can resolve Discover-forwarded seeds.
  Object.values(flowState).forEach((stepData) => {
    if (!stepData?.option) return;
    const options = Array.isArray(stepData.option)
      ? stepData.option
      : [stepData.option];
    options.forEach((option) => {
      if (!option.customField) return;
      const key = option.customField.toLowerCase();
      if (customFieldValues[key]) return;
      const value = getOptionValueString(option);
      if (value) customFieldValues[key] = value;
    });
  });

  return customFieldValues;
};

/**
 * Translates placeholders in a step based on current flow state and context
 * Creates a copy of the step to avoid mutating the original
 * @param step - The step to translate
 * @param context - Context containing flow state, steps, dcs, and audience intent
 * @returns Translated step with placeholders replaced
 */
export const translateStepPlaceholders = (
  step: Step,
  context: TranslateStepContext,
): Step => {
  // Deep copy the step to avoid mutating the original from the service layer
  // This is important for nested objects like questions in MULTI_QUESTION steps
  const stepCopy = JSON.parse(JSON.stringify(step));

  // If no placeholders, return the copy as-is
  if (!stepCopy.placeholders || stepCopy.placeholders.length === 0) {
    return stepCopy;
  }

  const currentFieldValues = getCurrentFieldValues(
    context.steps,
    context.flowState,
  );

  const degree =
    getDataByFieldFromFlow('degree', context.flowState) ||
    context.dcs?.degree ||
    context.audienceIntent?.degreeDecisionOutcome ||
    '';
  const category =
    getDataByFieldFromFlow('category', context.flowState) ||
    context.dcs?.category ||
    '';
  const subject =
    getDataByFieldFromFlow('subject', context.flowState) ||
    context.dcs?.subject ||
    context.audienceIntent?.subjectDecisionOutcome ||
    '';
  const career =
    getDataByFieldFromFlow('career', context.flowState) ||
    context.audienceIntent?.careerDecisionOutcome ||
    '';

  // Collect custom field values from all steps
  const customFieldValues = getCustomFieldValues(
    context.steps,
    context.flowState,
  );

  const placeholdersToTranslateValues: Record<string, string> = {
    degree,
    category,
    subject,
    career,
    ...Object.keys(currentFieldValues).reduce(
      (acc, key) => {
        if (!['degree', 'category', 'subject', 'career'].includes(key)) {
          acc[key.toLowerCase()] = currentFieldValues[key];
        }
        return acc;
      },
      {} as Record<string, string>,
    ),
    // Add custom field values (e.g., goal, interest, etc.)
    ...customFieldValues,
  };

  return translateStringFields(
    stepCopy,
    stepCopy.placeholders as Placeholder[],
    placeholdersToTranslateValues,
  );
};
